import * as React from 'react';
import { useMemo, useState } from 'react';
import { BookOpen } from 'lucide-react';
import { Input } from '@/components/ui/input';

export interface KnowledgeBaseOption {
  id: string;
  name: string;
  type?: 'normal' | 'sharepoint' | 'google-drive' | 'outlook' | string | null;
}

interface KnowledgeBaseSelectionSectionProps {
  knowledgeBases: KnowledgeBaseOption[];
  isLoading: boolean;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  hideTitle?: boolean;
}

/**
 * Multi-select list for linking org shared knowledge bases (mirrors web project wizard section).
 */
export function KnowledgeBaseSelectionSection({
  knowledgeBases,
  isLoading,
  selectedIds,
  onSelectedIdsChange,
  hideTitle = false,
}: KnowledgeBaseSelectionSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredKBs = useMemo(() => {
    if (!searchQuery.trim()) return knowledgeBases;
    const q = searchQuery.toLowerCase().trim();
    return knowledgeBases.filter((kb) => kb.name.toLowerCase().includes(q));
  }, [knowledgeBases, searchQuery]);

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectedIdsChange(selectedIds.filter((x) => x !== id));
    } else {
      onSelectedIdsChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-2">
      {!hideTitle && (
        <label className="text-sm font-medium text-slate-700">Knowledge bases</label>
      )}
      <Input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search knowledge bases…"
        className="w-full text-sm"
      />
      <div className="border border-slate-200 rounded-lg max-h-[min(280px,45vh)] overflow-y-auto bg-white">
        {isLoading ? (
          <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
        ) : filteredKBs.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">
            {searchQuery.trim()
              ? 'No knowledge bases match your search.'
              : 'No knowledge bases available.'}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredKBs.map((kb) => (
              <label
                key={kb.id}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(kb.id)}
                  onChange={() => toggle(kb.id)}
                  className="rounded border-slate-300 text-primary focus:ring-primary/20"
                />
                <BookOpen size={18} className="text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-800">{kb.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
